import React, { useState, useEffect } from 'react';
import { 
  ShoppingBag, 
  DollarSign, 
  Package, 
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Users
} from 'lucide-react';
import { getAnalytics } from '../../services/firestoreService';

const Dashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    const data = await getAnalytics();
    setAnalytics(data);
    setLoading(false);
  };

  const stats = [
    {
      title: 'إجمالي الطلبات',
      value: analytics?.totalOrders || 0,
      icon: ShoppingBag,
      color: 'bg-blue-500',
      lightColor: 'bg-blue-100',
      textColor: 'text-blue-600'
    },
    {
      title: 'الإيرادات الكلية',
      value: `$${analytics?.totalRevenue || 0}`,
      icon: DollarSign,
      color: 'bg-green-500',
      lightColor: 'bg-green-100',
      textColor: 'text-green-600'
    },
    {
      title: 'المنتجات',
      value: analytics?.totalProducts || 0,
      icon: Package,
      color: 'bg-purple-500',
      lightColor: 'bg-purple-100',
      textColor: 'text-purple-600'
    },
    {
      title: 'الطلبات المكتملة',
      value: analytics?.completedOrders || 0,
      icon: CheckCircle,
      color: 'bg-yellow-500',
      lightColor: 'bg-yellow-100',
      textColor: 'text-yellow-600'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Title */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">لوحة التحكم الرئيسية</h1>
        <p className="text-gray-600 mt-2">نظرة عامة على أداء الموقع</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">{stat.title}</p>
                  <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div className={`${stat.lightColor} p-3 rounded-lg`}>
                  <Icon className={`w-8 h-8 ${stat.textColor}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">الطلبات الأخيرة</h2>
        
        {analytics?.recentOrders && analytics.recentOrders.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">رقم الطلب</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">العميل</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">النوع</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">المبلغ</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">الحالة</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {analytics.recentOrders.map((order, index) => (
                  <tr key={order.id || index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">#{order.id?.slice(0, 8)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{order.customerName || 'غير محدد'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{order.type || 'عام'}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">${order.total || 0}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium
                        ${order.status === 'completed' ? 'bg-green-100 text-green-700' : 
                          order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 
                          'bg-gray-100 text-gray-700'}`}>
                        {order.status === 'completed' ? 'مكتمل' : 
                         order.status === 'pending' ? 'قيد الانتظار' : 
                         'ملغي'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p>لا توجد طلبات حالياً</p>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <h3 className="text-lg font-bold mb-2">إضافة منتج جديد</h3>
          <p className="text-sm opacity-90 mb-4">أضف منتجات أو خدمات جديدة</p>
          <button className="bg-white text-blue-600 px-4 py-2 rounded-lg font-medium hover:bg-blue-50 transition-colors">
            إضافة الآن
          </button>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
          <h3 className="text-lg font-bold mb-2">إدارة الطلبات</h3>
          <p className="text-sm opacity-90 mb-4">عرض وإدارة جميع الطلبات</p>
          <button className="bg-white text-green-600 px-4 py-2 rounded-lg font-medium hover:bg-green-50 transition-colors">
            عرض الطلبات
          </button>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <h3 className="text-lg font-bold mb-2">التقارير</h3>
          <p className="text-sm opacity-90 mb-4">عرض التقارير والإحصائيات</p>
          <button className="bg-white text-purple-600 px-4 py-2 rounded-lg font-medium hover:bg-purple-50 transition-colors">
            عرض التقارير
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
